import { useTracking } from '@vp/react-tracking';
import React from 'react';
import { Card, FlexBox, Typography, responsive } from '@vp/swan';
import { Request } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { STANDARD_PREVIEW_IMAGE } from '../utils/constants';
import { getAnalyticsData } from '../utils/getAnalyticsData';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';

const ResponsiveFlexBox = responsive(FlexBox);

export const JobCard = ({ request }: { request: Request }) => {
    const { tracking } = useTracking();

    const standardDescription = __(
        'Designers whose skills and preferences are a good fit for your project will now review your brief and let you know they are interested.'
    );

    const autoDescription = __(
        `Hi there! We'll begin working on your design soon. You will be notified by email when your design is ready for review.`,
        {
            _context: `This text will be shown to customers in My Design Services for projects that are being matched with a designer and don't require any actions from them at that time.`,
        }
    );

    const matchingUrl = `/design/matching/${request.facts?.jobId}/applicants`;

    const ctas: CTA[] = [
        {
            label: __('Select designer'),
            iconType: 'designerMarketplace',
            href: matchingUrl,
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData(matchingUrl, 'select-designer', 'past'),
                    action: 'Navigation Clicked',
                    navigationDetail: 'Select Designer',
                });
            },
            openInNewTab: false,
            skin: 'primary',
        },
    ];

    if (!request.job) {
        throw new Error('no job data');
    }

    const { type, title, createdAt } = request.job;
    const auto: boolean = type === 'JOB_TYPE_AUTO';

    return (
        <div>
            <Card bordered>
                <ResponsiveFlexBox
                    xs={{
                        flexDirection: 'column',
                        gap: 'between-subsections',
                        alignItems: 'center',
                    }}
                    sm={{
                        flexDirection: 'row',
                        gap: 'between-sections',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Project image thumbnail */}
                    <RequestCardImage
                        {...(auto
                            ? {
                                  imgSrc: STANDARD_PREVIEW_IMAGE,
                                  imgAlt: __('Design Preview'),
                              }
                            : {})}
                    />
                    {/* Project details */}
                    <ResponsiveFlexBox
                        style={{ flex: '1 1 100%' }}
                        xs={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'between-subsections',
                        }}
                        sm={{ alignItems: 'flex-start' }}
                    >
                        <ResponsiveFlexBox
                            xs={{
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'between-actions',
                            }}
                            sm={{ alignItems: 'flex-start' }}
                        >
                            <RequestCardSummary
                                title={title}
                                createdAt={createdAt}
                                {...(auto
                                    ? {
                                          status: 'COLLABORATION_STATUS_REQUEST_SUBMITTED',
                                          requestType: 'JobTypeAuto',
                                      }
                                    : {
                                          linkUrl: matchingUrl,
                                          productName: request.expertService?.title,
                                      })}
                            />
                            <Typography fontSkin="body-standard">
                                {auto ? autoDescription : standardDescription}
                            </Typography>
                        </ResponsiveFlexBox>
                    </ResponsiveFlexBox>
                    {!auto && <RequestCardCTA ctas={ctas} />}
                </ResponsiveFlexBox>
            </Card>
        </div>
    );
};
